import { useEffect, useMemo } from 'react'
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom'
import { pushVisitedPage } from '../helpers/navigationHistory'

/**
 * The title a header's static `titles` map gives the current route, plus the
 * navigation-history entry for it.
 *
 * Detail pages record themselves through useSetPageTitle; the top-level nav routes
 * covered by the maps never call it, so without this they would be missing from the
 * breadcrumb trail entirely. The map only ever fills a gap: the entry is pushed with
 * `keepExistingTitle`, so a page that sets its own, more specific title keeps it
 * whichever of the two effects runs first — the desktop header renders after the
 * routed page in MainLayout, the mobile one before it.
 */
export function useRouteTitle(titles: Record<string, string>): string | undefined {
  const { pathname, search } = useLocation()

  const routeTitle = useMemo(
    () => titles[pathname] || titles[pathname.split('/')[1]],
    [titles, pathname],
  )

  const navigationType = useNavigationType()

  useEffect(() => {
    if (routeTitle) {
      pushVisitedPage(
        { pathname, title: routeTitle, search },
        { keepExistingTitle: true, replace: navigationType === NavigationType.Replace },
      )
    }
  }, [pathname, search, routeTitle, navigationType])

  return routeTitle
}
